import { useLanguage } from '../context/LanguageContext';
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Building2, CheckCircle, ArrowLeft } from 'lucide-react';
import { apiClient, formatApiError, useAuth } from '../context/AuthContext';
import { useTurnstile } from '../components/Turnstile';
import toast from 'react-hot-toast';

export default function ProRegister() {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    company_name: '', first_name: '', last_name: '', email: '', phone: '',
    siret: '', activity: '', website: '', message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { isAuthenticated, isPro, user } = useAuth();
  const { getToken } = useTurnstile();
  const navigate = useNavigate();

  // Pré-remplir les champs si l'utilisateur est connecté
  useEffect(() => {
    if (isAuthenticated && user) {
      setForm(prev => ({
        ...prev,
        first_name: user.first_name || (user.name ? user.name.split(' ')[0] : '') || '',
        last_name: user.last_name || (user.name ? user.name.split(' ').slice(1).join(' ') : '') || '',
        email: user.email || '',
        phone: user.phone || '',
        company_name: user.company || ''
      }));
    }
  }, [isAuthenticated, user]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const ttoken = await getToken();
      await apiClient.post('/pro-requests', form, {
        headers: ttoken ? { 'X-Turnstile-Token': ttoken } : {}
      });
      setSubmitted(true);
      toast.success('Demande envoyée !');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (isPro) {
    return (
      <div className="min-h-screen bg-[#f5f7fa] flex items-center justify-center px-6">
        <div className="text-center max-w-md bg-white rounded-3xl border border-gray-100 p-10 shadow-sm">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={32} className="text-green-500" />
          </div>
          <h2 className="text-2xl font-black text-gray-900 mb-4">Votre compte est déjà validé en Pro</h2>
          <p className="text-gray-600 mb-6">Vous profitez actuellement des tarifs préférentiels. Vous pouvez consulter les prix directement depuis le catalogue.</p>
          <Link to="/catalogue" className="inline-flex items-center gap-2 bg-[#00aaff] text-white px-6 py-3 rounded-full font-bold hover:bg-[#0088dd] transition-colors">
            Voir le catalogue
          </Link>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={32} className="text-green-500" />
          </div>
          <h2 className="text-2xl font-black text-gray-900 mb-3">Demande envoyée !</h2>
          <p className="text-gray-500 mb-3">Votre demande d'accès pro a bien été reçue.</p>
          <p className="text-gray-600 font-medium mb-6">Notre équipe vous contactera sous 24-48h.</p>
          <Link to="/" className="inline-flex items-center gap-2 text-[#00aaff] font-semibold hover:underline">
            <ArrowLeft size={16} /> Retour à l'accueil
          </Link>
        </div>
      </div>
    );
  }

  // Formulaire complet pour tous (utilisateurs connectés ou non)
  return (
    <div className="min-h-screen bg-[#f5f7fa] px-6 py-16" data-testid="pro-register-page">
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-[#00aaff] text-sm mb-8 transition-colors">
          <ArrowLeft size={16} /> Retour à l'accueil
        </Link>

        <div className="bg-white rounded-3xl border border-gray-100 p-8 md:p-12 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-[#e0f4ff] rounded-xl flex items-center justify-center">
              <Building2 size={20} className="text-[#00aaff]" />
            </div>
            <span className="text-xs text-[#00aaff] font-semibold uppercase tracking-widest">Pro</span>
          </div>
          <h1 className="text-3xl font-black text-gray-900 mb-2">Devenir revendeur</h1>
          <p className="text-gray-500 mb-8">Accédez aux tarifs grossiste et à votre espace dédié.</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wider">Société *</label>
                <input name="company_name" value={form.company_name} onChange={handleChange} required
                  placeholder="Joey Distribution SARL"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wider">Prénom *</label>
                <input name="first_name" value={form.first_name} onChange={handleChange} required
                  placeholder="Jean"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wider">Nom *</label>
                <input name="last_name" value={form.last_name} onChange={handleChange} required
                  placeholder="Dupont"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wider">Email pro *</label>
                <input name="email" type="email" value={form.email} onChange={handleChange} required
                  placeholder="contact@societe.fr"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wider">Téléphone</label>
                <input name="phone" value={form.phone} onChange={handleChange}
                  placeholder="+33 6 00 00 00 00"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wider">SIRET</label>
                <input name="siret" value={form.siret} onChange={handleChange}
                  placeholder="123 456 789 00012"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wider">Secteur d'activité</label>
                <input name="activity" value={form.activity} onChange={handleChange}
                  placeholder="Optique, High-tech..."
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wider">Site web</label>
              <input name="website" value={form.website} onChange={handleChange}
                placeholder="https://www.societe.fr"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors" />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wider">Message</label>
              <textarea name="message" value={form.message} onChange={handleChange} rows={3}
                placeholder="Décrivez votre projet..."
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors resize-none" />
            </div>

            <button type="submit" disabled={loading}
              className="w-full bg-[#00aaff] text-white py-3.5 rounded-full font-bold text-sm flex items-center justify-center gap-2 hover:bg-[#0088dd] transition-colors disabled:opacity-60">
              {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : null}
              {loading ? "Envoi en cours..." : "Envoyer ma demande"}
            </button>

            {isAuthenticated ? null : (
              <p className="text-center text-xs text-gray-400 mt-4">
                Déjà un compte pro ?{' '}
                <Link to="/login" className="text-[#00aaff] hover:underline font-semibold">Se connecter</Link>
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
